import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../helpers/utils/Constants/route";
import { useAppDispatch } from "../../../store/store";
import {
  fetchInterviewDetails,
  fetchFeedback,
  fetchEvaluations,
  fetchAudio,
} from "../../../store/features/AdminPanel/InterviewLogDetail/InterviewLogDetailApi";
import InterviewRubricTab from "./tabs/InterviewRubricTab";
import QuestionAnalysisTab from "./tabs/QuestionAnalysisTab";
import TranscriptTab from "./tabs/TranscriptTab";
import { formatDate } from "./utils/interviewUtils";
import "./interviewLogDetailContent.scss";

interface InterviewLogDetailContentProps {
  interviewId: string;
}

const InterviewLogDetailContent: React.FC<InterviewLogDetailContentProps> = ({
  interviewId,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [interview, setInterview] = useState<any | null>(null);
  const [feedback, setFeedback] = useState<any | null>(null);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [audioData, setAudioData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>("rubric");

  useEffect(() => {
    if (interviewId) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId]);

  const fetchAllData = async () => {
    if (!interviewId) return;

    try {
      setLoading(true);
      const [
        interviewAction,
        feedbackAction,
        evaluationsAction,
        audioAction,
      ] = await Promise.allSettled([
        dispatch(fetchInterviewDetails(interviewId)),
        dispatch(fetchFeedback(interviewId)),
        dispatch(fetchEvaluations(interviewId)),
        dispatch(fetchAudio(interviewId)),
      ]);

      if (
        interviewAction.status === "fulfilled" &&
        interviewAction.value?.payload
      ) {
        const interviewData = interviewAction.value.payload as any;
        if (interviewData?.success && interviewData?.data) {
          setInterview(interviewData.data);
        } else if (interviewData?.data) {
          setInterview(interviewData.data);
        }
      }

      if (
        feedbackAction.status === "fulfilled" &&
        feedbackAction.value?.payload
      ) {
        const feedbackData = feedbackAction.value.payload as any;
        if (feedbackData?.success && feedbackData?.data) {
          setFeedback(feedbackData.data);
        } else if (feedbackData?.data) {
          setFeedback(feedbackData.data);
        }
      }

      if (
        evaluationsAction.status === "fulfilled" &&
        evaluationsAction.value?.payload
      ) {
        const evaluationsData = evaluationsAction.value.payload as any;
        if (evaluationsData?.success && evaluationsData?.data) {
          setEvaluations(evaluationsData.data);
        } else if (evaluationsData?.data) {
          setEvaluations(evaluationsData.data);
        } else if (Array.isArray(evaluationsData)) {
          setEvaluations(evaluationsData);
        }
      }

      if (audioAction.status === "fulfilled" && audioAction.value?.payload) {
        const audioDataResult = audioAction.value.payload as any;
        if (audioDataResult?.success && audioDataResult?.data) {
          setAudioData(audioDataResult.data);
        } else if (audioDataResult?.data) {
          setAudioData(audioDataResult.data);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="interview-detail-content">
        <div className="loading-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="interview-detail-content">
        <div className="error-container">
          <h2>Interview not found</h2>
          <button type="button" onClick={() => navigate(ROUTES.INTERVIEW_LOGS)}>
            Back to Interview Logs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="interview-detail-content">
      <div className="detail-header-section">
        <div className="header-content">
          <h1 className="main-title">Interview Analysis Report</h1>
          <p className="subtitle">
            {interview.role || interview.interviewType} - {formatDate(interview.date || interview.createdAt)}
          </p>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-wrapper">
          <button
            type="button"
            className={`tab-button ${activeTab === "rubric" ? "active" : ""}`}
            onClick={() => setActiveTab("rubric")}
          >
            Interview Rubric
          </button>
          <button
            type="button"
            className={`tab-button ${
              activeTab === "questions" ? "active" : ""
            }`}
            onClick={() => setActiveTab("questions")}
          >
            Question Analysis
          </button>
          <button
            type="button"
            className={`tab-button ${
              activeTab === "transcript" ? "active" : ""
            }`}
            onClick={() => setActiveTab("transcript")}
          >
            Transcript
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "rubric" && (
          <InterviewRubricTab
            feedback={feedback}
            evaluations={evaluations}
            interview={interview}
          />
        )}
        {activeTab === "questions" && (
          <QuestionAnalysisTab
            evaluations={evaluations}
            interview={interview}
          />
        )}
        {activeTab === "transcript" && (
          <TranscriptTab interview={interview} audioData={audioData} />
        )}
      </div>
    </div>
  );
};

export default InterviewLogDetailContent;

